import { useEffect, useMemo, useState } from 'react';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import ModalWrapper from '@/components/General/ModalWrapper';
import Input from '@/components/General/inputs/Input';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { saveFormFolder } from '@/data/forms';
import { descendantFolderIDs, folderPath } from '@/utils/formFolders';

export default function FormFolderModal() {
    const { id, parentFolderID } = useLocalSearchParams();
    const router = useRouter();
    const queryClient = useQueryClient();
    const { data: folders = [] } = Query.formFolders.all();
    const existing = folders.find((folder) => folder.id === id);
    const [name, setName] = useState('');
    const [parentID, setParentID] = useState(parentFolderID || null);

    useEffect(() => {
        if (existing) {
            setName(existing.name);
            setParentID(existing.parentFolderID || null);
        }
    }, [existing]);

    const choices = useMemo(() => {
        const excluded = id ? descendantFolderIDs(folders, id) : new Set();
        return folders
            .filter((folder) => !excluded.has(folder.id))
            .map((folder) => ({ ...folder, name: folderPath(folders, folder.id) }));
    }, [folders, id]);
    const selectedParent = choices.find((folder) => folder.id === parentID) || null;
    const choiceQuery = { data: choices, isLoading: false, isFetching: false, refetch: () => {} };

    const mutation = useMutation({
        mutationFn: saveFormFolder,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.formFolders.all() });
            router.back();
        },
    });

    const save = () => {
        if (!name.trim()) {
            Toast.show({ type: 'error', text2: 'Enter a folder name.' });
            return;
        }
        mutation.mutate({ id, name, parentFolderID: parentID });
    };

    return (
        <ModalWrapper title={id ? 'Rename Form Folder' : 'New Form Folder'} confirmText="Save" onConfirm={save} loading={mutation.isPending}>
            <KeyboardAwareScrollView keyboardShouldPersistTaps="handled">
                <Input title="Folder Name" value={name} onUpdate={setName} maxLength={120} testID="form-folder-name" />
                <SheetSelector
                    title="Parent Folder"
                    placeholder="Top level"
                    type="roleFolders"
                    queryResult={choiceQuery}
                    value={selectedParent}
                    onChange={(folder) => setParentID(folder?.id || null)}
                    testID="form-folder-parent"
                />
            </KeyboardAwareScrollView>
        </ModalWrapper>
    );
}
